import dot from 'dot-prop-immutable';
import { pathOr } from 'ramda';

import { types } from './actions';
import { CHANNEL_TYPES } from './constants';

const initialState = {
  activeChannelId: null,
  activeChannelType: null,
  activeThreadByChannels: {},
  annotationInputStates: [],
  annotationsCreatingForChannelIds: [],
  annotationsCreatedForChannelIds: [],
  annotationTool: {
    channelId: null,
    channelType: null,
    tool: null,
  },
  chatDrawerState: null,
  commentInputStates: [],
  commentsCreatedForAnnotationIds: [],
  commentsCreatingForAnnotationIds: [],
  editCommentActive: {},
  editCommentSaving: {},
  isChatDrawerOpen: false,
  isCreatingChannelAndAnnotation: false,
  isExportControlled: false,
  isFetchingSuppliersMscSettings: false,
  isInitialStateLoading: true,
  isOwner: false,
  isReinitializingTrustedThirdPartyChannel: false,
  isUpdatingMscConfig: false,
  partMasterVersion: null,
  source: null,
  suppliersMscSettings: null,
  techDataPackageRootUuid: null,
};

const reducer = (state = initialState, action) => {
  const { payload, type } = action;
  switch (type) {
    case types.CHAT_APP_RESET:
      return {
        ...initialState,
      };
    case types.ANNOTATION_INPUT_RECORD_EDITOR_STATE: {
      /*
      Remove existing input states for an input then append additional ones.

      The first annotation for a PRIMARY or MSC channel there will be no id,
      so we check by channel id first and if its not there fall back to channel type.
      */
      let nextAnnotationInputStates;
      if (
        (payload.channelType === CHANNEL_TYPES.MSC && !payload.channelId) ||
        (payload.channelType === CHANNEL_TYPES.PRIMARY && !payload.channelId)
      ) {
        nextAnnotationInputStates = state.annotationInputStates.filter(
          (annotationInputState) =>
            annotationInputState.channelType != payload.channelType,
        );
      } else {
        nextAnnotationInputStates = state.annotationInputStates.filter(
          (annotationInputState) =>
            annotationInputState.channelId != payload.channelId,
        );
      }
      return {
        ...state,
        annotationInputStates: nextAnnotationInputStates.concat([
          {
            channelId: payload.channelId,
            channelType: payload.channelType,
            editorState: payload.editorState,
          },
        ]),
      };
    }
    case types.ANNOTATION_TOOL_SET_ACTIVE:
      return {
        ...state,
        annotationTool: {
          channelId: payload.channelId,
          channelType: payload.channelType,
          tool: payload.tool,
        },
      };
    case types.ANNOTATION_TOOL_SET_INACTIVE:
      return {
        ...state,
        annotationTool: {
          channelId: null,
          channelType: null,
          tool: null,
        },
      };
    case types.CHAT_DRAWER_CLOSE:
      return {
        ...state,
        isChatDrawerOpen: false,
      };
    case types.CHAT_DRAWER_OPEN:
      return {
        ...state,
        isChatDrawerOpen: true,
      };
    case types.CHAT_DRAWER_STATE_SET:
      return {
        ...state,
        activeChannelId: payload.activeChannelId,
        activeChannelType: payload.activeChannelType,
        chatDrawerState: payload.chatDrawerState,
        isInitialStateLoading: false,
      };
    case types.CHAT_APP_INIT:
      return {
        ...initialState,
        isChatDrawerOpen: true,
        isInitialStateLoading: true,
        isOwner: payload.isOwner,
        isExportControlled: payload.isExportControlled,
        partMasterVersion: payload.partMasterVersion,
        source: payload.source,
        techDataPackageRootUuid: payload.techDataPackageRootUuid,
      };
    case types.CHAT_PANEL_INIT:
      return {
        ...state,
        isInitialStateLoading: true,
        isOwner: payload.isOwner,
        isExportControlled: payload.isExportControlled,
        partMasterVersion: payload.partMasterVersion,
        source: payload.source,
        techDataPackageRootUuid: payload.techDataPackageRootUuid,
      };
    case types.COMMENT_INPUT_RECORD_EDITOR_STATE: {
      const nextCommentInputStates = state.commentInputStates.filter(
        (commeintInputState) =>
          commeintInputState.annotationId != payload.annotationId,
      );

      return {
        ...state,
        commentInputStates: nextCommentInputStates.concat([
          {
            annotationId: payload.annotationId,
            editorState: payload.editorState,
          },
        ]),
      };
    }
    case types.CREATE_ANNOTATION_ERROR: {
      // for annotations being created for new channels (ie. channelId = null) we will clear all annotationsCreating records
      let nextAnnotationsCreatingForChannelIds = [];
      if (payload.channelId) {
        nextAnnotationsCreatingForChannelIds =
          state.annotationsCreatingForChannelIds.filter(
            (id) => payload.channelId != id,
          );
      }
      return {
        ...state,
        annotationsCreatingForChannelIds: nextAnnotationsCreatingForChannelIds,
        isCreatingChannelAndAnnotation: false,
      };
    }
    case types.CREATE_ANNOTATION_INPUT_REINITIALIZED:
      return {
        ...state,
        annotationsCreatedForChannelIds:
          state.annotationsCreatedForChannelIds.filter(
            (id) => id != payload.channelId,
          ),
      };
    case types.CREATE_ANNOTATION_SUCCESS: {
      const { channelId, channelType } = payload;

      const nextAnnotationsCreatedForChannelIds = channelId
        ? state.annotationsCreatedForChannelIds.concat([Number(channelId)])
        : state.annotationsCreatedForChannelIds.concat([]);

      const nextAnnotationInputStates = state.annotationInputStates.filter(
        (inputState) => {
          if (channelType == CHANNEL_TYPES.SECONDARY) {
            return inputState.channelId != channelId;
          } else {
            return inputState.channelType != channelType;
          }
        },
      );

      return {
        ...state,
        annotationInputStates: nextAnnotationInputStates,
        annotationsCreatingForChannelIds:
          state.annotationsCreatingForChannelIds.filter(
            (id) => id != channelId,
          ),
        annotationsCreatedForChannelIds: nextAnnotationsCreatedForChannelIds,
        isCreatingChannelAndAnnotation: false,
      };
    }
    case types.CREATE_ANNOTATION_RECORD_ANNOATION_CREATING:
    case types.CREATE_ANNOTATION_REQUEST: {
      const { channelId } = payload;
      const isCreatingChannelAndAnnotation = channelId ? false : true;
      const annotationsCreatingForChannelIds = channelId
        ? state.annotationsCreatingForChannelIds.concat([Number(channelId)])
        : state.annotationsCreatingForChannelIds.concat([]);
      return {
        ...state,
        annotationsCreatingForChannelIds,
        isCreatingChannelAndAnnotation,
      };
    }
    case types.CREATE_COMMENT_ERROR:
      return {
        ...state,
        commentsCreatingForAnnotationIds:
          state.commentsCreatingForAnnotationIds.filter(
            (annotationId) => annotationId != payload.annotationId,
          ),
      };
    case types.CREATE_COMMENT_INPUT_REINITIALIZED:
      return {
        ...state,
        commentsCreatedForAnnotationIds:
          state.commentsCreatedForAnnotationIds.filter(
            (id) => id != payload.annotationId,
          ),
      };
    case types.CREATE_COMMENT_REQUEST: {
      return {
        ...state,
        commentsCreatingForAnnotationIds:
          state.commentsCreatingForAnnotationIds.concat([
            Number(payload.annotationId),
          ]),
      };
    }
    case types.CREATE_COMMENT_SUCCESS: {
      const { annotationId } = payload;

      const nextCommentsCreatedForAnnotationIds = annotationId
        ? state.commentsCreatedForAnnotationIds.concat([Number(annotationId)])
        : state.commentsCreatedForAnnotationIds.concat([]);

      const nextCommentInputStates = state.commentInputStates.filter(
        (commentInputState) => commentInputState.annotationId != annotationId,
      );

      return {
        ...state,
        commentInputStates: nextCommentInputStates,
        commentsCreatingForAnnotationIds:
          state.commentsCreatingForAnnotationIds.filter(
            (id) => annotationId != id,
          ),
        commentsCreatedForAnnotationIds: nextCommentsCreatedForAnnotationIds,
      };
    }
    case types.EDIT_COMMENT_CANCEL_BY_ID:
      return dot.merge(state, 'editCommentActive', {
        [payload.commentId]: false,
      });
    case types.EDIT_COMMENT_ERROR:
      return dot.merge(state, 'editCommentSaving', {
        [payload.commentId]: false,
      });
    case types.EDIT_COMMENT_REQUEST:
      return dot.merge(state, 'editCommentSaving', {
        [payload.commentId]: true,
      });
    case types.EDIT_COMMENT_SET_BY_ID:
      return dot.merge(state, 'editCommentActive', {
        [payload.commentId]: true,
      });
    case types.EDIT_COMMENT_SUCCESS:
      return {
        ...state,
        editCommentActive: dot.set(
          state.editCommentActive,
          payload.commentId,
          false,
        ),
        editCommentSaving: dot.set(
          state.editCommentSaving,
          payload.commentId,
          false,
        ),
      };
    case types.EDIT_MSC_CHANNEL_CONFIG_ERROR:
    case types.EDIT_MSC_CHANNEL_CONFIG_SUCCESS:
      return {
        ...state,
        isUpdatingMscConfig: false,
      };
    case types.EDIT_MSC_CHANNEL_CONFIG_REQUEST:
      return {
        ...state,
        isUpdatingMscConfig: true,
      };
    case types.FETCH_SUPPLIERS_MSC_SETTINGS_ERROR:
      return {
        ...state,
        isFetchingSuppliersMscSettings: false,
      };
    case types.FETCH_SUPPLIERS_MSC_SETTINGS_REQUEST:
      return {
        ...state,
        isFetchingSuppliersMscSettings: true,
      };
    case types.FETCH_SUPPLIERS_MSC_SETTINGS_SUCCESS:
      return {
        ...state,
        isFetchingSuppliersMscSettings: false,
        suppliersMscSettings: payload.suppliersMscSettings,
      };
    case types.THREAD_SET_ACTIVE:
      return {
        ...state,
        activeThreadByChannels: dot.set(
          state.activeThreadByChannels,
          payload.channelId,
          payload.annotationId,
        ),
      };
    case types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_ERROR:
    case types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_SUCCESS:
      return {
        ...state,
        isReinitializingTrustedThirdPartyChannel: false,
      };
    case types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_REQUEST:
      return {
        ...state,
        isReinitializingTrustedThirdPartyChannel: true,
      };
    case types.THREAD_SET_INACTIVE:
      return {
        ...state,
        activeThreadByChannels: dot.set(
          state.activeThreadByChannels,
          payload.channelId,
          null,
        ),
      };
    default:
      return state;
  }
};

const getScope = (state) => state.modules.common.chat.chat;

export const getActiveChannelId = (state) => getScope(state).activeChannelId;

export const getActiveChannelType = (state) =>
  getScope(state).activeChannelType;

export const getAnnotationToolState = (state) => getScope(state).annotationTool;

export const getActiveThreadByChannel = (state, channelId = null) =>
  pathOr(null, [channelId], getScope(state).activeThreadByChannels);

export const getActiveThread = (state) => {
  const activeChannelId = getActiveChannelId(state);
  return getActiveThreadByChannel(state, activeChannelId);
};

export const getAnnotationInputStateForChannel = (
  state,
  channelId,
  channelType,
) => {
  if (channelId) {
    return getScope(state).annotationInputStates.find(
      (inputState) => inputState.channelId == channelId,
    );
  } else {
    return getScope(state).annotationInputStates.find(
      (inputState) => inputState.channelType == channelType,
    );
  }
};

export const getChatDrawerState = (state) => getScope(state).chatDrawerState;

export const getCommentInputStateForAnnotation = (state, annotationId) =>
  getScope(state).commentInputStates.find(
    (inputState) => inputState.annotationId == annotationId,
  );

export const isCommentCreatingForAnnotation = (state, annotationId) =>
  getScope(state).commentsCreatingForAnnotationIds.includes(
    Number(annotationId),
  );

export const isCommentBeingEdited = (state, commentId) =>
  pathOr(false, ['editCommentActive', commentId], getScope(state));

export const isCommentSaving = (state, commentId) =>
  pathOr(false, ['editCommentSaving', commentId], getScope(state));

export const hasCreatedCommentForAnnotation = (state, annotationId) =>
  getScope(state).commentsCreatedForAnnotationIds.includes(
    Number(annotationId),
  );

export const isCreatingAnnotationForChannel = (state, channelId = null) => {
  if (channelId) {
    return getScope(state).annotationsCreatingForChannelIds.includes(
      Number(channelId),
    );
  } else {
    return getScope(state).isCreatingChannelAndAnnotation;
  }
};

export const isReinitializingTrustedThirdPartyChannel = (state) =>
  getScope(state).isReinitializingTrustedThirdPartyChannel;

export const getIsOwner = (state) => getScope(state).isOwner;

export const getIsFetchingSuppliersMscSettings = (state) =>
  getScope(state).isFetchingSuppliersMscSettings;

export const getIsUpdatingMscConfig = (state) =>
  getScope(state).isUpdatingMscConfig;

export const hasCreatedAnnotationForChannel = (state, channelId = null) => {
  if (channelId) {
    return getScope(state).annotationsCreatedForChannelIds.includes(
      Number(channelId),
    );
  } else {
    // TODO - handle this case
    return getScope(state).isCreatingChannelAndAnnotation;
  }
};

export const isChatDrawerOpen = (state) => getScope(state).isChatDrawerOpen;

export const isCreatingAnnotation = (state) =>
  getScope(state).annotationsCreatingForChannelIds.length > 0;

export const isCreatingComment = (state) =>
  getScope(state).commentsCreatingForAnnotationIds.length > 0;

export const getIsInitialStateLoading = (state) =>
  getScope(state).isInitialStateLoading;

export const getSource = (state) => getScope(state).source;

export const getSuppliersMscSettings = (state) =>
  getScope(state).suppliersMscSettings;

export const getTechDataPackageRootUuid = (state) =>
  getScope(state).techDataPackageRootUuid;

export const getPartMasterVersion = (state) =>
  getScope(state).partMasterVersion;

export const getIsExportControlled = (state) =>
  getScope(state).isExportControlled;

export default reducer;
